import { z } from 'zod';
import type {
    ValorBinLookupCredentialSecretV1,
    DraftValorCredentials,
    ValorCredentials,
    ValorCredentialsPublic,
    ValorCredentialsSecret,
} from '../../../types/ipg/valor/valor-credential.types';
import {
    buildStoredJsonValueParser,
    defineStoredJsonContract,
} from '../../../stored-json-contract.types';

export const ValorCredentialsPublicSchema: z.ZodObject<{
    domain: z.ZodString;
    channel: z.ZodString;
    epi: z.ZodString;
}> = z
    .object({
        domain: z.string().url(),
        channel: z.string().min(1),
        epi: z.string().min(1),
    })
    .strict() satisfies z.ZodType<ValorCredentialsPublic>;

export const ValorCredentialsSecretSchema: z.ZodObject<{
    appid: z.ZodString;
    appkey: z.ZodString;
}> = z
    .object({
        appid: z.string().min(1),
        appkey: z.string().min(1),
    })
    .strict() satisfies z.ZodType<ValorCredentialsSecret>;

export const ValorCredentialsSchema: z.ZodType<ValorCredentials> =
    ValorCredentialsPublicSchema.merge(ValorCredentialsSecretSchema);

export const ValorBinLookupCredentialSecretV1Schema: z.ZodType<ValorBinLookupCredentialSecretV1> = z
    .object({
        version: z.literal('v1'),
        credentials: ValorCredentialsSchema,
    })
    .strict();

export const ValorBinLookupCredentialSecretStorageContract = defineStoredJsonContract({
    contractName: 'ValorBinLookupCredentialSecret',
    semanticFieldValues: {},
    variants: {
        v1: {
            schemaName: 'ValorBinLookupCredentialSecretV1Schema',
            schema: ValorBinLookupCredentialSecretV1Schema,
            guards: [
                {
                    kind: 'json_marker_equals',
                    markerPath: ['version'],
                    markerValue: 'v1',
                },
            ],
            normalize: (payload: ValorBinLookupCredentialSecretV1): ValorCredentials =>
                payload.credentials,
            semanticPaths: {},
            unrepresentedSemanticFields: [],
        },
    },
    writerVariant: 'v1',
});

export const ValorBinLookupCredentialSecretParser: z.ZodType<ValorCredentials> =
    buildStoredJsonValueParser(ValorBinLookupCredentialSecretStorageContract);

export const DraftValorCredentialsSchema: z.ZodType<DraftValorCredentials> = z
    .object({
        domain: z.string().url().optional(),
        epi: z.string().min(1).optional(),
        appid: z.string().min(1).optional(),
        appkey: z.string().min(1).optional(),
        channel: z.string().min(1).optional(),
    })
    .strict()
    .refine((value) => Object.keys(value).length > 0, {
        message: 'At least one credential field is required',
    });
